import { type OfflineQueuedMessage, getDiscussionDB } from "@/lib/discussion-db";
export type { OfflineQueuedMessage };

const createdAtTimestamp = (value: string) => {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortByCreatedAtFallback = (items: OfflineQueuedMessage[]) =>
  [...items].sort((left, right) => {
    const leftTime = createdAtTimestamp(left.createdAt);
    const rightTime = createdAtTimestamp(right.createdAt);
    if (leftTime !== rightTime) return leftTime - rightTime;
    return left.id.localeCompare(right.id);
  });

const normalizeThreadQueueOrder = async (
  db: ReturnType<typeof getDiscussionDB>,
  threadId: number,
) => {
  const items = await db.queued_messages.where("threadId").equals(threadId).toArray();
  if (items.length === 0) return [];

  const allHaveSequence = items.every((item) => typeof item.sequence === "number");
  if (allHaveSequence) {
    return [...items].sort((left, right) => {
      if ((left.sequence ?? 0) !== (right.sequence ?? 0)) {
        return (left.sequence ?? 0) - (right.sequence ?? 0);
      }

      const leftTime = createdAtTimestamp(left.createdAt);
      const rightTime = createdAtTimestamp(right.createdAt);
      if (leftTime !== rightTime) return leftTime - rightTime;

      return left.id.localeCompare(right.id);
    });
  }

  const normalized = sortByCreatedAtFallback(items).map((item, index) => ({
    ...item,
    sequence: index + 1,
  }));

  await db.queued_messages.bulkPut(normalized);

  return normalized;
};
const loadAll = async () => {
  const db = getDiscussionDB();
  return db.queued_messages.toArray();
};
export const setOfflineMessages = async (items: OfflineQueuedMessage[]) => {
  const db = getDiscussionDB();
  await db.transaction("rw", db.queued_messages, async () => {
    await db.queued_messages.clear();
    await db.queued_messages.bulkPut(items);
  });
  return items;
};
export const getOfflineMessagesForThread = async (threadId: number) => {
  const db = getDiscussionDB();
  return normalizeThreadQueueOrder(db, threadId);
};
export const enqueueOfflineMessage = async (item: OfflineQueuedMessage) => {
  const db = getDiscussionDB();

  await db.transaction("rw", db.queued_messages, async () => {
    const threadQueue = await normalizeThreadQueueOrder(db, item.threadId);
    const nextSequence =
      threadQueue.length === 0
        ? 1
        : (threadQueue[threadQueue.length - 1]?.sequence ?? threadQueue.length) + 1;

    await db.queued_messages.put({
      ...item,
      sequence: typeof item.sequence === "number" ? item.sequence : nextSequence,
    });
  });

  return loadAll();
};
export const removeOfflineMessage = async (id: string) => {
  const db = getDiscussionDB();
  await db.queued_messages.delete(id);
  return loadAll();
};
export const clearOfflineMessagesForThread = async (threadId: number) => {
  const db = getDiscussionDB();
  await db.transaction("rw", db.queued_messages, async () => {
    await db.queued_messages.where("threadId").equals(threadId).delete();
  });
  return loadAll();
};